import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

import { Search } from 'lucide-react';
import { Dialog, Text } from 'components/Elements';

export const SystemCurrency = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return Cookies.get('systemCurrency') || 'USD';
  });
  const [currencyCountryMap, setCurrencyCountryMap] = useState({});

  useEffect(() => {
    // Fetch currencies and country data
    const fetchCurrencyData = async () => {
      try {
        // Fetch currency data from exchangerate-api.com
        const currencyResponse = await fetch(
          'https://api.exchangerate-api.com/v4/latest/USD'
        );
        const currencyData = await currencyResponse.json();
        const currencyList = Object.keys(currencyData.rates).map((code) => ({
          code,
          name:
            new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ||
            code,
        }));
        setCurrencies(currencyList);

        // Fetch country and currency mapping from restcountries.com
        const countriesResponse = await fetch(
          'https://restcountries.com/v3.1/all'
        );
        const countriesData = await countriesResponse.json();
        const map = {};

        countriesData.forEach((country) => {
          if (country.currencies) {
            Object.keys(country.currencies).forEach((currencyCode) => {
              map[currencyCode] = country.cca2.toLowerCase(); // Country code (ISO 3166-1 alpha-2) used for flag images
            });
          }
        });

        setCurrencyCountryMap(map);
      } catch (error) {
        console.error('Error fetching currency or country data:', error);
      }
    };
    fetchCurrencyData();
  }, []);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch(
          'https://api.exchangerate-api.com/v4/latest/USD'
        );
        const data = await response.json();
        if (data.rates) {
          const currencyList = Object.keys(data.rates).map((code) => ({
            code,
            name:
              new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ||
              code,
          }));
          setCurrencies(currencyList);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    };
    fetchCurrencies();
  }, []);

  const selectCurrency = (code) => {
    setSelectedCurrency(code);
    Cookies.set('systemCurrency', code, { expires: 365 });
    setIsOpen(false);
  };

  const getCurrencyFlag = (code) => {
    const countryCode = currencyCountryMap[code];
    return countryCode ? `https://flagcdn.com/w20/${countryCode}.png` : '';
  };

  const filteredCurrencies = currencies.filter(
    (currency) =>
      currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(true)}
        className="px-4 py-2 rounded-md space-x-2 text-gray-200 font-medium flex items-center"
      >
        <Text>{selectedCurrency}</Text>
        <img
          src={getCurrencyFlag(selectedCurrency)}
          alt={`${selectedCurrency} flag`}
          className="w-6 h-4"
        />
      </button>

      <Dialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className="inline-block align-bottom bg-white rounded-2xl px-6 pt-5 pb-6 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Select system currency
          </h2>
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search currency..."
              className="w-full px-4 py-2 border rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              className="absolute right-3 top-3 text-gray-500"
              size={18}
            />
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredCurrencies.map((currency) => (
              <button
                key={currency.code}
                onClick={() => selectCurrency(currency.code)}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded-md"
              >
                {currency.name} ({currency.code})
              </button>
            ))}
          </div>
        </div>
      </Dialog>
    </div>
  );
};
